import { useCallback, useEffect, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import participationRequestsApi, {
  type ParticipationRequest,
} from '@/src/api/participationRequestsApi';

/**
 * useFetchParticipationRequest — `GET /me/participation-requests/{id}`, once.
 *
 * The whole fetch behind `/requests/[id]` (TicketTier's "View Request"
 * action, and where confirm lands right after applying). No poll, unlike
 * [[usePollBooking]]: nothing here changes on its own in the seconds after a
 * request is created — a verdict only lands once an organizer reviews it on
 * Backstage — so `retry` is a plain manual re-ask, not a wait loop.
 */
export type FetchParticipationRequestState =
  | { status: 'loading'; request: null; error: null }
  | { status: 'error'; request: null; error: PSErrorClass }
  | { status: 'success'; request: ParticipationRequest; error: null };

/**
 * A settled result tagged with the `id` it's for — the same shape
 * [[useFetchEvent]] uses, and for the same reason: the public state is
 * DERIVED from it, so a result for a stale `id` reads as `loading` without
 * the effect ever calling `setState` synchronously on its own body.
 */
type SettledResult =
  | { status: 'error'; request: null; error: PSErrorClass; id: string }
  | {
      status: 'success';
      request: ParticipationRequest;
      error: null;
      id: string;
    };

const LOADING = { status: 'loading', request: null, error: null } as const;

export function useFetchParticipationRequest(
  id: string
): FetchParticipationRequestState & { retry: () => void } {
  const [result, setResult] = useState<SettledResult | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;

    participationRequestsApi
      .getParticipationRequest(id)
      .then((request) => {
        if (active) setResult({ status: 'success', request, error: null, id });
      })
      .catch((error: unknown) => {
        if (active)
          setResult({
            status: 'error',
            request: null,
            error: asApiError(error),
            id,
          });
      });

    return () => {
      active = false;
    };
  }, [id, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Derived: loading until a result for THIS id has settled.
  const state = result && result.id === id ? result : LOADING;

  return { ...state, retry };
}
